import { emitHintSignal } from '@flobi/asset-library/hints';
import { toParent } from '../space';
import { ROOT_ID, cloneCount, clonerSpec, effectorsLeftBehind, expandCloner, newId, type Rect } from '@flobi/artwork-core';
import type { ToolContext } from './types';
import { RECIPES, spacingOf, type Recipe } from './effectors';

/**
 * Repeat: what a cloner does, said in the person's words and done from wherever they are.
 *
 * Tarek's pick A (2026-10-01, https://claude.ai/artifact/XHBMutSiz1FuEDxxrAZP1s): Repeat
 * is a button on the chosen thing, in the Edit dock, and its popover holds the
 * arrangement, its numbers and the variations (`shell/RepeatPanel.tsx`). The Cloner tool's
 * strip does the same things; both call these, so the two can never disagree about what
 * Repeat, Make Layers, Stop Repeating or a variation does.
 */

/**
 * Past this many layers, Make Layers is doing somebody a disservice. Counted inside each
 * copy: a copy of a group is the group and everything in it.
 */
export const MOST_EXPANDED = 10_000;

/**
 * The most Make Layers' one edit may weigh. Every edit goes to the other editors and the
 * server as one message, and a message over 10 MB is cut off by the gateway — a few copies
 * of an imported 1,600-path drawing are 3 MB each, before base64 adds a third.
 */
export const MOST_EXPANDED_BYTES = 6 * 1024 * 1024;

/** What a cloner's copies can be made into, given the two ceilings above. */
export function expandable(scene: ToolContext['scene'], id: string, into: 'group' | 'parent', prefix = '') {
  return expandCloner(scene, id, { into, most: MOST_EXPANDED, mostBytes: MOST_EXPANDED_BYTES, prefix });
}

/**
 * Why a cloner's copies were not made into layers, with the count that would work. It used
 * to say "This makes 2005 copies" of a cloner of seven: the copies left out added to the
 * ceiling, which counts layers (Farah, 2026-10-22).
 */
export function tooManyCopies(expansion: { kept: number; dropped: number }, what: string): string {
  const all = expansion.kept + expansion.dropped;
  if (expansion.kept === 0) return `Even one copy is too big to ${what} at once. Simplify what is being repeated first.`;
  return `Only ${expansion.kept} of these ${all} copies can ${what} at once. Lower the count to ${expansion.kept} first.`;
}

/**
 * Why an edit was refused, in the words a person can act on.
 *
 * A locked layer is the usual reason, and the one worth naming — every batch is all or
 * nothing and refuses a locked node, so Release and Expand did nothing while the layer
 * was locked, without a word (QA 2026-09-24, cloner-F18).
 */
export function refusal(errors: readonly { code: string; message: string }[], what: string): string {
  const locked = errors.find((error) => error.code === 'locked');
  if (locked) return `${what} — a layer in it is locked. Unlock it first.`;
  return `${what}.`;
}

/** What can be repeated: anything that draws. Never an artboard, a slice or an effector. */
export function repeatable(type: string | undefined): boolean {
  return !!type && type !== 'artboard' && type !== 'slice' && type !== 'effector';
}

/**
 * Wraps what is chosen in a cloner, sized so the first array is worth looking at, and
 * chooses it. Answers the cloner's id, or null when nothing could be repeated.
 */
export function wrapInCloner(ctx: ToolContext): string | null {
  // Never an effector, any more than an artboard or a slice: it draws nothing, so a
  // cloner of one draws nothing either — while its box, as big as the field, still
  // takes every click over it (QA 2026-09-24, cloner-F6).
  const ids = ctx.selection.filter((id) => repeatable(ctx.node(id)?.type));
  if (ids.length === 0) return null;
  // Whatever was in hand before is its own step: Undo takes back the wrapping alone.
  ctx.stopCapturing();
  let box: Rect | null = null;
  for (const id of ids) {
    const one = ctx.boundsOf(id);
    if (!one) continue;
    box = box
      ? {
          x: Math.min(box.x, one.x),
          y: Math.min(box.y, one.y),
          width: Math.max(box.x + box.width, one.x + one.width) - Math.min(box.x, one.x),
          height: Math.max(box.y + box.height, one.y + one.height) - Math.min(box.y, one.y),
        }
      : one;
  }
  if (!box) return null;
  const middle = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
  const order = ctx.scene.paintOrder();
  const top = [...ids].sort((a, b) => order.indexOf(a) - order.indexOf(b))[ids.length - 1]!;
  // A step of the source's own size and a bit, so what appears is an array rather than
  // a heap: a first guess nobody has to undo before they can see what they made.
  // Held to the schema's limit: past 80,000 units wide the guess ran over it, the whole
  // batch was refused, and Shift+C did nothing at all (QA 2026-09-24, cloner-F21).
  const stepX = Math.round(Math.min(100_000, Math.max(8, box.width * 1.25)));
  const stepY = Math.round(Math.min(100_000, Math.max(8, box.height * 1.25)));
  const result = ctx.edit([
    {
      op: 'create',
      ref: 'cloner',
      parentId: ctx.scene.parentOf(top) ?? ROOT_ID,
      node: {
        type: 'cloner',
        name: 'Cloner',
        transform: toParent(ctx.scene, ctx.scene.parentOf(top), [1, 0, 0, 1, middle.x, middle.y]),
        mode: 'grid',
        countX: 4,
        countY: 3,
        stepX,
        stepY,
        radius: Math.round(Math.max(40, Math.max(box.width, box.height) * 1.6)),
      },
    },
    ...ids.map((id) => ({ op: 'move' as const, id, parentId: '$cloner', position: 'front' as const, keepWorldTransform: true })),
  ]);
  ctx.stopCapturing();
  // Said rather than swallowed: a wrap that was refused used to leave the page as it was
  // with nothing to say why (cloner-F21).
  if (!result.ok) {
    ctx.notice(refusal(result.errors, 'A cloner couldn’t be created'));
    return null;
  }
  const made = result.created.cloner ?? null;
  if (made) ctx.setSelection([made]);
  return made;
}

/**
 * Make Layers: bakes the array into real layers and stops being a repeat.
 *
 * Exactly what was on the screen — `expandCloner` reads the same plan the painter draws,
 * effectors and shades included — as a group holding one layer per copy, so Ungroup
 * hands each copy back to be changed by itself.
 */
export function makeLayers(ctx: ToolContext, id: string): boolean {
  const expansion = expandable(ctx.scene, id, 'group');
  const { ops, made, dropped } = expansion;
  // Refused rather than cut short: expanding the first 2,000 and deleting the cloner
  // threw the rest away without a word (QA 2026-09-24, cloner-F11).
  if (dropped > 0) {
    ctx.notice(tooManyCopies(expansion, 'be made into layers'));
    return false;
  }
  if (ops.length === 0) return false;
  // The settings changed on the way here are their own steps: one Undo after this brings
  // the repeat back as it was a moment ago, not as it was before all of them
  // (QA 2026-09-24, cloner-F2).
  ctx.stopCapturing();
  // Its effectors go with it when nothing else names them: they push nothing now, and
  // were left behind in Layers (QA 2026-09-24, cloner-F16). What it held counts as
  // staying: its copies are rebuilt from it, a cloner among them naming the same effectors.
  const leftBehind = effectorsLeftBehind(ctx.scene, [id], ctx.scene.childrenOf(id)).map((each) => ({ op: 'delete' as const, id: each }));
  const result = ctx.edit([...ops, { op: 'delete', id }, ...leftBehind] as Parameters<ToolContext['edit']>[0]);
  if (!result.ok) {
    ctx.notice(refusal(result.errors, 'The copies couldn’t be made into layers'));
    return false;
  }
  ctx.setSelection(made.map((ref) => result.created[ref]).filter((one): one is string => Boolean(one)));
  ctx.stopCapturing();
  return true;
}

/** Stop Repeating: takes the array away and hands back what was being copied. */
export function stopRepeating(ctx: ToolContext, id: string): boolean {
  const parent = ctx.scene.parentOf(id) ?? ROOT_ID;
  const kids = ctx.scene.childrenOf(id);
  if (kids.length === 0) return false;
  // Its own undo step, apart from the settings changed before it (cloner-F2).
  ctx.stopCapturing();
  const result = ctx.edit([
    ...kids.map((child) => ({ op: 'move' as const, id: child, parentId: parent, position: 'front' as const, keepWorldTransform: true })),
    { op: 'delete' as const, id },
    // And the effectors only it named, which push nothing now (cloner-F16).
    ...effectorsLeftBehind(ctx.scene, [id], kids).map((each) => ({ op: 'delete' as const, id: each })),
  ]);
  // Refused (a locked layer): say so, and leave the selection where it was rather than
  // moving it as if the release had happened (cloner-F18).
  if (!result.ok) {
    ctx.notice(refusal(result.errors, 'The cloner couldn’t be removed'));
    return false;
  }
  ctx.setSelection([...kids]);
  ctx.stopCapturing();
  return true;
}

/**
 * Gives a cloner a variation made from a recipe, put where the array is so it reaches it
 * straight away. Answers the effector's id.
 *
 * Sized and placed to cover the arrangement, because an effector that lands somewhere
 * else does nothing at all — and "it did nothing" is indistinguishable from "it is
 * broken" the first time somebody presses the button. Its amounts are scaled to how far
 * apart the copies are, for the same reason: a scatter of nine units on a grid of three
 * hundred is not a scatter, it is a rounding error.
 *
 * `choose` picks the new effector (the Cloner tool's strip opens on it); the dock's
 * popover keeps the repeat chosen, since it lists the variations itself.
 */
export function addVariation(ctx: ToolContext, id: string, recipe: Recipe, { choose = true } = {}): string | null {
  const node = ctx.node(id);
  if (node?.type !== 'cloner') return null;
  const box = ctx.boundsOf(id);
  if (!box) return null;
  const wide = Math.max(40, Math.round(box.width));
  const tall = Math.max(40, Math.round(box.height));
  const props = recipe.make(spacingOf(box, cloneCount(clonerSpec(node))));
  // Its own undo step, apart from the settings changed before it (cloner-F2).
  ctx.stopCapturing();
  /**
   * Made and named in ONE batch, with the names of effectors that are gone dropped.
   *
   * The list is capped at sixteen and nothing took a deleted effector's id off it, so
   * after sixteen add-and-delete rounds the naming edit was refused — in a second batch
   * whose answer was ignored — and every new effector was made and did nothing
   * (QA 2026-09-24, cloner-F17). The id is chosen here because a create cannot name
   * another create's id inside a property.
   */
  const made = newId();
  const kept = (node.effectors ?? []).filter((each) => ctx.node(each)?.type === 'effector');
  if (kept.length >= 16) {
    ctx.notice('This already has 16 variations — the most one cloner can have.');
    return null;
  }
  const result = ctx.edit([
    {
      op: 'create',
      parentId: ctx.scene.parentOf(id) ?? ROOT_ID,
      node: {
        id: made,
        type: 'effector',
        // Named after what it does, which is what Layers, the strip and the popover call it.
        name: recipe.name,
        transform: toParent(ctx.scene, ctx.scene.parentOf(id), [1, 0, 0, 1, box.x, box.y]),
        width: wide,
        height: tall,
        ...props,
      },
    },
    { op: 'set', id, props: { effectors: [...kept, made] } },
  ]);
  if (!result.ok) {
    ctx.notice(refusal(result.errors, 'The variation couldn’t be added'));
    return null;
  }
  if (choose) ctx.setSelection([made]);
  ctx.stopCapturing();
  // From the Cloner's strip or the Repeat popover: an effector added, so its hint is done (lib/hints).
  emitHintSignal('effector.added');
  return made;
}

/**
 * Takes a variation away and puts the copies back as they were, in one step.
 *
 * Its name goes off the cloner's list in the same batch: left there, it is a name with
 * nothing behind it, counted against the sixteen and listened for by every cache.
 */
export function removeVariation(ctx: ToolContext, effectorId: string): string | null {
  const owner = ownerOf(ctx, effectorId);
  ctx.stopCapturing();
  const ownerNode = owner ? ctx.node(owner) : null;
  const result = ctx.edit([
    { op: 'delete', id: effectorId },
    ...(owner && ownerNode?.type === 'cloner' ? [{ op: 'set', id: owner, props: { effectors: (ownerNode.effectors ?? []).filter((each) => each !== effectorId) } }] : []),
  ]);
  if (!result.ok) {
    ctx.notice(refusal(result.errors, 'The variation couldn’t be removed'));
    return null;
  }
  // Back to the array it was pushing, which is where the person was a moment ago.
  ctx.setSelection(owner ? [owner] : []);
  ctx.stopCapturing();
  return owner;
}

/**
 * Names shapes so no two in one list read the same: the first Rectangle keeps its name, the
 * next is "Rectangle 2". A list of "Path, Path, Rectangle, Rectangle" asked somebody to guess.
 */
export function distinctNames<T extends { name: string }>(list: readonly T[]): T[] {
  const seen = new Map<string, number>();
  return list.map((one) => {
    const count = (seen.get(one.name) ?? 0) + 1;
    seen.set(one.name, count);
    return count === 1 ? one : { ...one, name: `${one.name} ${count}` };
  });
}

/** The cloner an effector belongs to. */
export function ownerOf(ctx: Pick<ToolContext, 'scene' | 'node'>, effectorId: string): string | null {
  for (const id of ctx.scene.paintOrder()) {
    const node = ctx.node(id);
    if (node?.type === 'cloner' && (node.effectors ?? []).includes(effectorId)) return id;
  }
  return null;
}

/** The recipe a variation was started from, by the name it was given; null for one renamed or made otherwise. */
export const recipeNamed = (name: string | undefined): Recipe | null => RECIPES.find((recipe) => recipe.name === name) ?? null;
